import type { Question } from '../../types';
import { HAB_MH_CONDITION, LOSS_RUN_LINE_OPTIONS } from './shared-options';

// ---------------------------------------------------------------------------
// Loss Runs Supplement
// Source: Centered Partners MHMF intake — "Loss Runs Checklist" sheet.
// 5-year currently valued loss runs — by carrier, by line — are the single
// highest-leverage diligence item. Carriers will not quote without them.
// Implemented as a repeating group keyed on line of coverage.
// ---------------------------------------------------------------------------

export const lossRunsQuestions: Question[] = [
  {
    id: 'lr_can_provide',
    text: 'Can you provide 5-year currently valued loss runs, by line and by carrier?',
    subtext:
      'This is the single most important diligence item — we cannot get firm quotes without them. Request them from your current broker or carriers now if you don’t have them.',
    section: 'loss_history',
    group: 'loss_runs',
    sort_order: 500,
    field_type: 'select',
    options: [
      { label: 'Yes — I have them ready', value: 'have' },
      { label: 'I can request them', value: 'will_request' },
      { label: 'I need help getting them', value: 'need_help' },
    ],
    required: true,
    field_path: 'loss_runs.can_provide',
    conditions: HAB_MH_CONDITION,
    tags: ['loss_runs'],
  },
  {
    id: 'lr_summary',
    text: '5-year loss summary by line of coverage',
    subtext:
      'One entry per line you currently carry. Use your best figures — we’ll verify against the loss runs.',
    section: 'loss_history',
    group: 'loss_runs',
    sort_order: 510,
    field_type: 'repeating_group',
    required: true,
    field_path: 'loss_runs.summary',
    conditions: HAB_MH_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 14,
      add_button_label: 'Add another line of coverage',
      row_label: 'Line of coverage',
      fields: [
        {
          id: 'lr_s_line',
          text: 'Line of coverage',
          section: 'loss_history',
          sort_order: 10,
          field_type: 'select',
          options: LOSS_RUN_LINE_OPTIONS,
          required: true,
          field_path: 'line',
          conditions: [],
        },
        {
          id: 'lr_s_carrier',
          text: 'Carrier',
          section: 'loss_history',
          sort_order: 20,
          field_type: 'text',
          placeholder: 'Philadelphia',
          required: true,
          field_path: 'carrier',
          conditions: [],
        },
        {
          id: 'lr_s_claims',
          text: '# Claims (5 years)',
          section: 'loss_history',
          sort_order: 30,
          field_type: 'number',
          placeholder: '0',
          required: true,
          field_path: 'claim_count',
          conditions: [],
        },
        {
          id: 'lr_s_paid',
          text: 'Total paid (5 years)',
          section: 'loss_history',
          sort_order: 40,
          field_type: 'currency',
          placeholder: '0',
          required: true,
          field_path: 'total_paid',
          conditions: [{ field: 'claim_count', operator: 'gt', value: 0 }],
        },
        {
          id: 'lr_s_reserves',
          text: 'Open reserves',
          section: 'loss_history',
          sort_order: 50,
          field_type: 'currency',
          placeholder: '0',
          required: true,
          field_path: 'open_reserves',
          conditions: [{ field: 'claim_count', operator: 'gt', value: 0 }],
        },
        {
          id: 'lr_s_largest',
          text: 'Largest single loss',
          section: 'loss_history',
          sort_order: 60,
          field_type: 'currency',
          placeholder: '0',
          required: false,
          field_path: 'largest_loss',
          conditions: [{ field: 'claim_count', operator: 'gt', value: 0 }],
        },
        {
          id: 'lr_s_trend',
          text: 'Claim frequency trend',
          section: 'loss_history',
          sort_order: 70,
          field_type: 'select',
          options: [
            { label: 'Decreasing', value: 'decreasing' },
            { label: 'Flat', value: 'flat' },
            { label: 'Increasing', value: 'increasing' },
          ],
          required: false,
          field_path: 'frequency_trend',
          conditions: [{ field: 'claim_count', operator: 'gt', value: 0 }],
        },
      ],
    },
    tags: ['loss_runs', 'repeating'],
  },
];
